/** @jsx jsx */
import { jsx } from '@emotion/react';
import { daysFromNow, parseDateTime } from '@zapier/date';
import { Price } from '@zapier/currency';
import { Alert, Link } from '@zapier/design-system';

import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import type { BannerProps } from './types';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

export const shouldRender = (data: ResponseData) => {
  return data.currentAccount.isTrialingTeams;
};

export const Component = function TeamsTrialing(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const { data, currency } = props;
  const { currentAccount } = data;
  const willAutoUpgrade = currentAccount.teamsTrialAutoUpgrade;
  const canToggleAutoUpgrade =
    currentAccount.currentUserRole &&
    ['owner', 'admin', 'super_admin'].includes(currentAccount.currentUserRole);
  const nextPlanAmount = data.nextTeamPlan?.amount ?? -1;
  const interval = currentAccount.plan.interval;
  const isYearly = interval === 'year';
  const monthlyAmount = isYearly ? nextPlanAmount / 12 : nextPlanAmount;
  const trialDaysRemaining = currentAccount.teamTrialEndDate
    ? daysFromNow(currentAccount.teamTrialEndDate)
    : 0;

  const formattedEndDate = parseDateTime(currentAccount.teamTrialEndDate!)
    .toLocaleString('en-US', { month: 'short', day: 'numeric' })
    .replace(/([a-z]+)/i, '$1.');

  const autoBillingLink = (
    <Link href={getFullyQualifiedURL('/app/settings/billing')}>
      Edit trial payment settings
    </Link>
  );

  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
  const title = currentAccount.teamTrialEndDate
    ? `${trialDaysRemaining} ${daysText} left in your Team trial`
    : 'You are on a Team trial';
  const intervalText = isYearly ? ' (paid annually)' : '';
  const content = willAutoUpgrade ? (
    <p data-zds>
      {`You will be billed and charged `}
      {monthlyAmount < 0 ? (
        ''
      ) : (
        <span data-zds>
          <Price currency={currency}>{monthlyAmount}</Price>/month
          {intervalText}
        </span>
      )}
      {'. '}
      {canToggleAutoUpgrade ? autoBillingLink : null}
    </p>
  ) : (
    <p data-zds>
      {(currentAccount.teamTrialEndDate
        ? `On ${formattedEndDate}, `
        : `At the end of your trial, `) +
        `you’ll lose access to Team plan features and be downgraded to a ${
          data.currentAccount?.plan?.type ?? 'Free'
        } plan. `}
      {canToggleAutoUpgrade ? autoBillingLink : null}
    </p>
  );

  return (
    <Alert layout={props.layout} title={title} type="info">
      {content}
    </Alert>
  );
};
